import { z } from 'zod';

const trustPresenceStatus = z.enum(['OFFLINE', 'ONLINE']);
const trustCategoryScores = z.object({
    GENERAL: z.number().optional(),
    USER_ATTENTION: z.number().optional(),
});
const trustSourceScores = z.object({
    MEMBER: z.number().optional(),
    AUTOMATIC: z.number().optional(),
});
const trustSourceCounts = z.object({
    MEMBER: z.number().optional(),
    AUTOMATIC: z.number().optional(),
});
const trustFeedback = z.object({
    overallScore: z.number(),
    categoryScores: trustCategoryScores,
    receivedCount: z.number(),
    sourceScores: trustSourceScores,
    sourceCounts: trustSourceCounts,
});
const trustBadgeTipAction = z.object({
    text: z.string(),
    link: z.string(),
});
const trustBadgeTip = z.object({
    text: z.string(),
    action: z.nullable(trustBadgeTipAction),
});
const trustReputation = z.object({
    feedback: z.nullable(trustFeedback),
});
const trustIdentity = z.object({
    name: z.string().nullable(),
    avatar: z.string().nullable(),
    memberSince: z.string().nullable(),
    description: z.string().nullable(),
    localProfileId: z.string().nullable(),
    municipality: z.string().nullable(),
    region: z.string().nullable(),
    verified: z.boolean(),
});
const trustCommunication = z.object({
    replyTimeText: z.string().nullable(),
    replyTimeInMinutes: z.number().nullable(),
});
const trustPresence = z.object({
    status: z.nullable(trustPresenceStatus),
    presenceText: z.string().nullable(),
    lastActivity: z.string().nullable(),
    enabled: z.boolean(),
});
const trustBadge = z.object({
    id: z.string(),
    amount: z.number(),
    text: z.string(),
    tip: trustBadgeTip,
});
const trustProfileInfo = z.object({
    reputation: trustReputation,
    identity: trustIdentity,
    communication: trustCommunication,
    presence: trustPresence,
    badges: z.array(trustBadge),
    links: z.object({
        self: z.object({
            href: z.string(),
        }),
    }),
    replyTimeText: z.string().nullable(),
});
const trustFeedbackUserToken = z.object({
    token: z.string(),
});
const pickABuyerLocked = z.object({
    locked: z.boolean(),
});
const trustProfileSettings = z.object({
    externalUserId: z.string(),
    enabled: z.boolean(),
});
z.string();
const trustFeedbackLinks = z.record(z.string(), z.object({
    href: z.string(),
    templated: z.boolean().optional(),
}));
const trustFeedbackActor = z.object({
    resourceId: z.string(),
    externalUserId: z.string(),
});
const trustFeedbackQuestionBase = z.object({
    key: z.number(),
    category: z.string(),
    question: z.string(),
    questionHelpText: z.string(),
    index: z.number(),
    skippable: z.boolean(),
    positiveAnswer: z.string(),
    negativeAnswer: z.string(),
    _links: trustFeedbackLinks,
});
const trustFeedbackScaleQuestion = trustFeedbackQuestionBase.extend({
    type: z.literal('SCALE'),
});
const trustFeedbackMultiselectQuestion = trustFeedbackQuestionBase.extend({
    type: z.literal('MULTISELECT'),
    options: z.array(z.object({
        id: z.string(),
        title: z.string(),
    })),
});
const trustFeedbackQuestion = z.union([
    trustFeedbackMultiselectQuestion,
    trustFeedbackScaleQuestion,
]);
const trustFeedbackAnswer = z.object({
    questionKey: z.number(),
    rating: z.number(),
    scalePoints: z.number(),
    scaleRating: z.number(),
    skipped: z.boolean(),
    unsupported: z.boolean(),
    options: z.array(z.object({ id: z.string() })).optional(),
});
const trustTextReviewSettings = z.object({
    title: z.string(),
    subtitle: z.string().optional(),
    threshold: z.number().nullable(),
    placeholder: z.string().nullable(),
    helper: z.string().nullable(),
    characterLimits: z.object({
        min: z.number(),
        max: z.number(),
    }),
});
const trustOpenFeedbackOnItem = z.object({
    key: z.number(),
    status: z.string(),
    item: z.object({
        id: z.string(),
    }),
    answers: z.array(trustFeedbackAnswer),
    textReview: z.string().nullable(),
    questions: z.array(trustFeedbackQuestion),
    textReviewConfiguration: z.enum([
        'ENABLED_MIN_SCORE',
        'ENABLED_MANDATORY_THRESHOLD',
        'ENABLED_MANDATORY',
        'ENABLED_OPTIONAL',
        'DISABLED',
    ]),
    textReviewMandatoryThreshold: z.number().nullable(),
    textReviewEnabledMinThreshold: z.number().nullable(),
    textReviewSettings: trustTextReviewSettings.nullable(),
    trade: z.object({
        status: z.string(),
        id: z.number(),
    }),
    tradeRole: z.enum(['SELLER', 'BUYER']),
    author: trustFeedbackActor,
    recipient: trustFeedbackActor,
    segmentExternalId: z.string(),
    featureToggles: z.object({
        autoSaverEnabled: z.boolean(),
    }),
    _links: trustFeedbackLinks,
});
const trustFeedbackAnswerDataBase = z.object({
    question: z.string(),
    skipped: z.boolean().optional(),
});
const trustFeedbackAnswerData = z.union([
    trustFeedbackAnswerDataBase.extend({
        rating: z.number(),
        scalePoints: z.number(),
    }),
    trustFeedbackAnswerDataBase.extend({
        options: z.array(z.object({ id: z.string() })),
    }),
]);
z.object({
    answers: z.array(trustFeedbackAnswerData),
    isComplete: z.boolean(),
});
const trustFeedbackEndpoints = z.object({
    _links: trustFeedbackLinks,
});
const trustTradeExists = z.object({
    tradeExists: z.boolean(),
});
const trustLead = z.object({
    avatarUrl: z.string().optional(),
    externalUserId: z.string(),
    name: z.string(),
    source: z.string(),
});
const trustLeads = z.array(trustLead);
z.object({
    buyerExternalId: z.string(),
    itemExternalId: z.string(),
    itemPictureUrl: z.string(),
    itemTitle: z.string(),
    segmentExternalId: z.string(),
    sellerExternalId: z.string(),
});
const trustCreatedTrade = z.object({
    tokens: z.union([
        z.object({
            sellerToken: z.string(),
        }),
        z.object({
            buyerToken: z.string(),
        }),
    ]),
    id: z.number(),
    status: z.string(),
    activation: z.string(),
    _links: trustFeedbackLinks,
});

export { pickABuyerLocked, trustBadge, trustBadgeTip, trustBadgeTipAction, trustCategoryScores, trustCommunication, trustCreatedTrade, trustFeedback, trustFeedbackActor, trustFeedbackAnswer, trustFeedbackAnswerData, trustFeedbackEndpoints, trustFeedbackMultiselectQuestion, trustFeedbackQuestion, trustFeedbackQuestionBase, trustFeedbackScaleQuestion, trustFeedbackUserToken, trustIdentity, trustLead, trustLeads, trustOpenFeedbackOnItem, trustPresence, trustPresenceStatus, trustProfileInfo, trustProfileSettings, trustReputation, trustSourceCounts, trustSourceScores, trustTextReviewSettings, trustTradeExists };
//# sourceMappingURL=zod.js.map
